import React, { useState, useEffect } from "react";
import { Card, Button, Form } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useRoomPackages } from "../../../hooks/useRoomPackages";
import { adminRestaurantPosService } from "../../../services/adminService";
import type { RoomPackage } from "../../../types";
import RoomPackageTable from "./RoomPackageTable";
import RoomPackageFormModal from "./RoomPackageFormModal";
import RoomPackageDetailModal from "./RoomPackageDetailModal";
// Backend room-package mutations use requireRole('admin', 'manager').
import { hasRole } from "../../../utils/permissions";

interface MenuCategory {
  id: string;
  name: string;
  is_active: boolean;
}

const canManage = (): boolean => hasRole("admin", "manager");

const RoomPackageManagement: React.FC = () => {
  const isManager = canManage();
  const { packages, loading, create, update, remove } = useRoomPackages();

  const [form] = Form.useForm();
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPackage, setEditingPackage] = useState<RoomPackage | null>(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<RoomPackage | null>(null);
  const [menuCategories, setMenuCategories] = useState<MenuCategory[]>([]);

  useEffect(() => {
    adminRestaurantPosService.getMenuCategories()
      .then((res) => setMenuCategories(Array.isArray(res.data) ? (res.data as MenuCategory[]) : []))
      .catch(() => {});
  }, []);

  const handleAdd = () => {
    setEditingPackage(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true, sort_order: 0, charge_basis: "per_person_per_night", inclusions: [] });
    setModalVisible(true);
  };

  const handleEdit = (pkg: RoomPackage) => {
    setEditingPackage(pkg);
    form.setFieldsValue({
      ...pkg,
      inclusions: pkg.inclusions || [],
    });
    setModalVisible(true);
  };

  const handleView = (pkg: RoomPackage) => {
    setSelectedPackage(pkg);
    setDetailsVisible(true);
  };

  const handleDelete = async (id: string) => {
    await remove(id);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingPackage) {
        await update(editingPackage.id, values);
      } else {
        await create(values);
      }
      setModalVisible(false);
      form.resetFields();
    } catch {
      // validation error, form shows inline
    }
  };

  const handleDetailDelete = async () => {
    if (selectedPackage) {
      await remove(selectedPackage.id);
      setDetailsVisible(false);
      setSelectedPackage(null);
    }
  };

  const handleDetailEdit = () => {
    if (selectedPackage) {
      setDetailsVisible(false);
      handleEdit(selectedPackage);
    }
  };

  return (
    <div>
      <Card
        title="Room Packages"
        extra={
          isManager ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              Add Package
            </Button>
          ) : null
        }
      >
        <RoomPackageTable
          packages={packages}
          loading={loading}
          canManage={isManager}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </Card>

      <RoomPackageFormModal
        open={modalVisible}
        editingPackage={editingPackage}
        loading={loading}
        form={form}
        menuCategories={menuCategories}
        onCancel={() => setModalVisible(false)}
        onOk={handleModalOk}
      />

      <RoomPackageDetailModal
        open={detailsVisible}
        pkg={selectedPackage}
        canManage={isManager}
        onCancel={() => setDetailsVisible(false)}
        onEdit={handleDetailEdit}
        onDelete={handleDetailDelete}
      />
    </div>
  );
};

export default RoomPackageManagement;
